import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum CarrierConnectionFailureReason {
  ADDRESS = "address",
  CREDENTIALS = "credentials",
  NO_RATES = "no_rates",
  UNKNOWN = "unknown",
}

/**
 * Whether a failure proves the account is misconfigured. No rates or an unknown error can come from the test
 * itself (a route the carrier doesn't serve, a slow sandbox), so those show "Couldn't confirm", not Not verified.
 */
const CONCLUSIVE_FAILURE: Record<CarrierConnectionFailureReason, boolean> = {
  [CarrierConnectionFailureReason.ADDRESS]: true,
  [CarrierConnectionFailureReason.CREDENTIALS]: true,
  [CarrierConnectionFailureReason.NO_RATES]: false,
  [CarrierConnectionFailureReason.UNKNOWN]: false,
};

export function isConclusiveCarrierFailure(
  reason: CarrierConnectionFailureReason,
): boolean {
  return CONCLUSIVE_FAILURE[reason];
}

/** Which address of the test shipment the carrier rejected. */
export enum CarrierConnectionAddressSide {
  FROM = "from",
  TO = "to",
  UNKNOWN = "unknown",
}

export const CarrierConnectionTestAddressSchema = z.object({
  name: z.string().nullish(),
  street1: z.string(),
  street2: z.string().nullish(),
  city: z.string(),
  state: z.string().nullish(),
  zip: z.string(),
  country: z.string(),
  phone: z.string().nullish(),
});
export interface CarrierConnectionTestAddress extends z.infer<
  typeof CarrierConnectionTestAddressSchema
> {}

const carrierConnectionTestOutcomeSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok"), rateCount: z.number() }),
  z.object({
    status: z.literal("failed"),
    reason: createEnum(CarrierConnectionFailureReason, {
      enumName: "CarrierConnectionFailureReason",
    }),
    addressSide: createEnum(CarrierConnectionAddressSide, {
      enumName: "CarrierConnectionAddressSide",
    }).nullable(),
    message: z.string(),
  }),
]);
const CarrierConnectionTestOutcomeSchema: z.ZodType<CarrierConnectionTestOutcome> =
  carrierConnectionTestOutcomeSchema;
export type CarrierConnectionTestOutcome = z.infer<
  typeof carrierConnectionTestOutcomeSchema
>;

/** The outcome plus the addresses that were tested, so a retry form can prefill them. */
export const CarrierConnectionTestResultSchema = z.object({
  outcome: CarrierConnectionTestOutcomeSchema,
  fromAddress: CarrierConnectionTestAddressSchema.nullable(),
  toAddress: CarrierConnectionTestAddressSchema,
});
export interface CarrierConnectionTestResult extends z.infer<
  typeof CarrierConnectionTestResultSchema
> {}

/**
 * Query param asking the carriers page to test an account on load. A connect flow that leaves the
 * page (an OAuth redirect) must set it, since the account is already listed when the page comes back.
 */
export const TEST_CARRIER_ACCOUNT_ID_PARAM = "testCarrierAccountId";

/** The merchant's own-carriers settings page, optionally asking it to test a just-connected account. */
export function merchantCarriersPageUrl({
  merchantAppUrl,
  teamId,
  testCarrierAccountId,
}: {
  merchantAppUrl: string;
  teamId: string;
  testCarrierAccountId?: string;
}): string {
  const params = new URLSearchParams({ carriersTab: "merchantCarriers" });
  if (testCarrierAccountId) {
    params.set(TEST_CARRIER_ACCOUNT_ID_PARAM, testCarrierAccountId);
  }
  return `${merchantAppUrl}/stores/${teamId}/settings/carriers?${params}`;
}
